import { Colors } from './colors';

export const LAYOUT = {
  navbar: { mobile: 56, desktop: 64 },
  /** Mobile bottom TabBar (polish round) — content needs this much bottom padding to clear it */
  tabBar: { height: 56, contentPadding: 110 },
  /**
   * Floating "+" button: 56px square, sits above the TabBar. Scrolling content
   * on a screen WITH a Fab needs `contentPadding` (bottom offset + size + a gap)
   * so the last item can scroll clear of the button.
   */
  fab: { size: 56, bottom: 104, right: 20, contentPadding: 104 + 56 + 16 },
  grid: {
    mobile: { columns: 1, gap: 16, padding: 20 },
    desktop: { columns: 4, gap: 20, padding: 40 },
  },
  /**
   * Maximum content width for desktop layouts
   * Prevents content from stretching too wide on large screens
   */
  maxContentWidth: 1200, // Standard max width for readability and visual balance
  /**
   * Content padding for horizontal spacing
   */
  contentPadding: {
    mobile: 16,
    desktop: 24,
  },
} as const;

/**
 * Spacing scale based on 4px grid system
 * Use these instead of arbitrary pixel values for consistent spacing
 *
 * Examples:
 * - padding: Spacing.base (16px)
 * - marginBottom: Spacing.xl (24px)
 * - gap: Spacing.sm (8px)
 */
export const Spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 20,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 48,
  '5xl': 64,
  '6xl': 80,
  '7xl': 96,
} as const;

/**
 * Type for spacing keys
 */
export type SpacingKey = keyof typeof Spacing;

/**
 * Border radius scale for consistent rounded corners
 *
 * Examples:
 * - borderRadius: BorderRadius.base (8px) - buttons, inputs
 * - borderRadius: BorderRadius.xl (12px) - cards
 * - borderRadius: BorderRadius.full (9999px) - circular avatars, FAB
 */
export const BorderRadius = {
  none: 0,
  xs: 3,
  sm: 4,
  md: 6,
  base: 8,
  lg: 10,
  xl: 12,
  '2xl': 14,
  '3xl': 16,
  '4xl': 18,
  '5xl': 20,
  full: 9999, // Fully circular (use with equal width/height)
} as const;

/**
 * Type for border radius keys
 */
export type BorderRadiusKey = keyof typeof BorderRadius;

/**
 * Semantic radii (design v2) — deliberately restrained: 4 / 6 / 8 / 12.
 * Round shapes (`Radii.full`) are reserved for what is round by nature:
 * avatars, club marks, toggles, radio dots, unread counts.
 *
 * - tag: chips and tags (condition, status, Admin), tab segments
 * - thumb: thumbnails, club-logo squares, menu rows, the app icon in the mobile header
 * - control: buttons (all sizes), inputs, selects, search, composer, photo tiles, filter chips
 * - card: cards, menus, deal card, icon squares in dialogs and empty states
 * - panel (hero / dialog / fab / bubble): hero, filter bar, dialogs, chat bubbles, the FAB, the login app icon
 */
export const Radii = {
  tag: BorderRadius.sm,          // 4
  thumb: BorderRadius.sm,        // 4
  control: BorderRadius.md,      // 6
  card: BorderRadius.base,       // 8
  panel: BorderRadius.xl,        // 12
  hero: BorderRadius.xl,         // 12 — alias of panel
  /** @deprecated design v2 uses the panel radius on mobile too */
  heroMobile: BorderRadius.xl,
  dialog: BorderRadius.xl,       // 12 — alias of panel
  fab: BorderRadius.xl,          // 12 — alias of panel
  bubble: BorderRadius.xl,       // 12 — alias of panel
  full: BorderRadius.full,       // avatars, club marks, toggles, radio dots, unread counts
} as const;

/**
 * Control heights: inputs, selects, buttons
 */
export const ControlHeight = {
  sm: 34,   // small buttons in section headers and the Member tag
  md: 48,   // inputs, selects, buttons
  lg: 52,   // hero search + hero buttons
} as const;

/**
 * @deprecated Use Colors from './colors' instead
 * Legacy export for backward compatibility - will be removed in future version
 */
export const COLORS = {
  primary: Colors.primary,
  background: Colors.background,
  text: Colors.text,
  white: Colors.white,
  border: Colors.border,
} as const;
